import {
  useSignReportsMutation,
  useProcessReportsMutation,
} from "app/store/transmission/api/transmissionTableApi";
import { useSaveClinicNotesMutation } from "app/store/transmission/api/transmissionNotesApi";

import {
  loadReports,
  toggleModal,
  selectedReport,
  toggleFetchTableData,
  setCacheRefresh,
  resetClinicNotes,
  setCurrentPage,
  setPdfPages,
  setPreviousReport,
  setExplicitReportNoteEditable,
  toggleReportSigning,
  toggleSignAndProcessing,
  toggleReportProcessing,
  setShowActionMenu,
  setFileToAttachToReport,
  setOriginalFileToAttachToReport,
  setFileToAttachSelected,
  setUpdatedTransmissionReportFile,
} from "app/store/transmission/slice/transmissionSlice";
import { removeCache } from "app/utils/cache";

import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import useCloseModalPage from "hooks/useCloseModalPage";
import { useDispatch, useSelector } from "react-redux";

import {
  showErrorToast,
  showSuccessToast,
  showToastWithUndo,
} from "utils/toast";
import { getRedirectPath } from "utils/routes.util";
import {
  USER_ROLES,
  EVOKE_ACTIVE,
  CLINIC_ACTIVE,
  SUBMIT_ACTIONS,
  EVOKE_PROCESSED,
  CLINIC_PROCESSED,
  CLINIC_FOR_REVIEW,
} from "static/constants";
import { getReportActionView } from "utils/footerView.util";

const useNotesFooter = (data) => {
  const { reports, reportList, reportId, reportDetail, reportCurrentTab } =
    useSelector((state) => state.transmission);
  const { currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reportId: reportIdFromUrl } = useParams();
  const closeReportPage = useCloseModalPage();

  const [signReports] = useSignReportsMutation();
  const [processReports] = useProcessReportsMutation();
  const [saveClinicNotes] = useSaveClinicNotesMutation();

  const getActionView = () =>
    getReportActionView({
      reportState: reportCurrentTab,
      role: currentUser?.role,
    });

  useEffect(() => {
    if (data) {
      dispatch(loadReports(data?._id));
    }
    //eslint-disable-next-line
  }, [data]);

  const getTabForUndo = (
    reportView,
    processState,
    eSig,
    isArchive,
    isClinicProcessedUnprocess
  ) => {
    const isProcess = processState === "process";
    const isUnprocess = processState === "unprocess";

    if (reportView === EVOKE_ACTIVE && isProcess) return EVOKE_PROCESSED;
    if (reportView === EVOKE_PROCESSED && isUnprocess) return EVOKE_ACTIVE;

    if (reportView === CLINIC_ACTIVE && isProcess) {
      return eSig || isArchive ? CLINIC_PROCESSED : CLINIC_FOR_REVIEW;
    }

    if (reportView === CLINIC_FOR_REVIEW) {
      if (isUnprocess) return CLINIC_ACTIVE;
      if (isProcess) return CLINIC_PROCESSED;
    }

    if (
      reportView === CLINIC_PROCESSED &&
      isUnprocess &&
      isClinicProcessedUnprocess
    ) {
      return currentUser?.role === USER_ROLES.PHYSICIAN
        ? CLINIC_FOR_REVIEW
        : CLINIC_ACTIVE;
    }

    return null;
  };

  const undoAction = async (
    removedReports,
    processState,
    isSign,
    isArchive,
    isClinicProcessedUnprocess,
    shouldSendLetter
  ) => {
    try {
      const newArr = reports.filter((report) =>
        removedReports.includes(report)
      );
      if (isSign) {
        const [note, fin] =
          reportDetail?.clinic_notes?.split("\n\nFIN: ") || [];

        await saveClinicNotes({
          reportId,
          clinicNotes: note,
          eSig: false,
          reportFIN: fin,
          physicianNotes: "",
          finUpdate: true,
        });
      }

      const isProcessing = processState === "process";

      const selectedReportTab = getActionView();

      const result = await processReports({
        reports: newArr,
        view: getTabForUndo(
          selectedReportTab,
          processState,
          reportDetail?.eSig,
          isArchive,
          isClinicProcessedUnprocess
        ),
        processModalState: isProcessing ? "unprocess" : "process",
        toReturn: !isProcessing,
        isSign,
        isClinicProcessedUnprocess,
        shouldSendLetter,
      }).unwrap();

      const { error, showError } = result;

      if (showError || error) {
        await showErrorToast(result?.message);
      } else {
        if (isSign) {
          openNextPdfModal(true);
        } else {
          dispatch(selectedReport(""));
          dispatch(toggleModal({ type: "reportDetail", value: false }));
        }

        showSuccessToast(`Undo Successful`);

        await removeCache();
        await dispatch(toggleFetchTableData(true));
        await dispatch(setCacheRefresh(true));
      }
    } catch (err) {
      showErrorToast(err?.data?.message);
    } finally {
      dispatch(toggleFetchTableData(false));
      dispatch(setCurrentPage(0));
      dispatch(setPdfPages(0));
      dispatch(resetClinicNotes());
      dispatch(setPreviousReport(""));
      dispatch(setExplicitReportNoteEditable(false));
    }
  };

  const closeReportModal = () => {
    dispatch(selectedReport(""));
    dispatch(toggleModal({ type: "reportDetail", value: false }));

    if (reportIdFromUrl) {
      closeReportPage({ keepTab: true });
    }
  };

  const openNextPdfModal = (isSignUndo) => {
    const currentReportIndex = reportList?.findIndex(
      (data) => data?._id === reportId
    );

    // Move on through the worklist only when the report was opened from it. reportList keeps the
    // last worklist shown, so a report on its own page (or one not in that list) just closes.
    const canMoveOn = !reportIdFromUrl && currentReportIndex >= 0;
    const nextReport = canMoveOn ? reportList[currentReportIndex + 1] : null;
    const prevReport = canMoveOn ? reportList[currentReportIndex - 1] : null;

    const targetReport = isSignUndo ? reportDetail : nextReport || prevReport;

    if (!targetReport) {
      dispatch(resetClinicNotes());

      if (reportIdFromUrl) {
        closeReportModal();
        return;
      }

      dispatch(selectedReport(""));
      dispatch(toggleModal({ type: "reportDetail", value: false }));
      navigate(getRedirectPath(), { replace: true });
      return;
    }

    dispatch(
      selectedReport({
        id: targetReport?._id,
        isLegacyReport: targetReport?.legacy ?? true,
        reportDetail: targetReport,
      })
    );
    dispatch(setCurrentPage(0));
    dispatch(setPdfPages(0));
    dispatch(resetClinicNotes());
    dispatch(setPreviousReport(""));
    dispatch(setExplicitReportNoteEditable(false));

    dispatch(setShowActionMenu(false));
    dispatch(setFileToAttachToReport(null));
    dispatch(setOriginalFileToAttachToReport(null));
    dispatch(setFileToAttachSelected(false));
    dispatch(setUpdatedTransmissionReportFile(null));
    dispatch(toggleModal({ type: "reportDetail", value: true }));
  };

  const handleProcess = async (
    processState,
    isSign = false,
    showProcessing = true,
    isArchive = false,
    isClinicProcessedUnprocess = false,
    shouldSendLetter = false
  ) => {
    const actionType = isArchive
      ? SUBMIT_ACTIONS.ARCHIVE
      : processState === "process"
        ? SUBMIT_ACTIONS.PROCESS
        : SUBMIT_ACTIONS.UNPROCESS;

    try {
      if (showProcessing) {
        dispatch(toggleReportProcessing({ type: actionType, value: true }));
      }
      dispatch(toggleModal({ type: processState, value: false }));

      const selectedReportTab = getActionView();

      const result = await processReports({
        reports: reports,
        view: selectedReportTab,
        processModalState: processState,
        toReturn: processState === "unprocess",
        shouldSendLetter,
        isArchive,
      }).unwrap();

      const { data, showError, message } = result;

      const undoProcessAction = async () => {
        await undoAction(
          data?.removedReports,
          processState,
          isSign,
          isArchive,
          isClinicProcessedUnprocess,
          shouldSendLetter
        );
      };

      if (showError) {
        await showErrorToast(result?.message);
      } else {
        if (data.removedReports?.length > 0) {
          await showToastWithUndo(message, undoProcessAction);

          if (isSign) {
            openNextPdfModal();
          } else {
            dispatch(setShowActionMenu(false));
            dispatch(setFileToAttachToReport(null));
            dispatch(setOriginalFileToAttachToReport(null));
            dispatch(setFileToAttachSelected(false));
            dispatch(setUpdatedTransmissionReportFile(null));
            closeReportModal();
          }

          await removeCache();
          await dispatch(toggleFetchTableData(true));
          await dispatch(setCacheRefresh(true));
        }
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      dispatch(setCurrentPage(0));
      dispatch(setPdfPages(0));
      dispatch(resetClinicNotes());
      dispatch(setPreviousReport(""));
      dispatch(toggleReportProcessing({ type: actionType, value: false }));
      dispatch(toggleFetchTableData(false));
      dispatch(setExplicitReportNoteEditable(false));
      dispatch(
        toggleModal({
          type: "physicianUnprocessFromClinicProcessed",
          value: false,
        })
      );
      dispatch(toggleReportSigning({ type: actionType, value: false }));
    }
  };

  const handleSignProcess = async (processState) => {
    const actionType =
      processState === "process"
        ? SUBMIT_ACTIONS.SIGN_AND_PROCESS
        : SUBMIT_ACTIONS.SIGN_AND_UNPROCESS;

    try {
      dispatch(toggleSignAndProcessing({ type: actionType, value: true }));

      await signReports([reportId]);

      await handleProcess(processState, true, false);
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      dispatch(toggleSignAndProcessing({ type: actionType, value: false }));
    }
  };

  return { handleProcess, handleSignProcess };
};

export default useNotesFooter;
